import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { ArgoCdApi } from './api';
import type { ArgoResourceTab } from './argocdUrl';
import { DiffView } from './DiffView';
import type {
  ArgoApplication,
  ArgoLogLine,
  ArgoResource,
  ArgoResourceAction,
  ArgoResourceDetails,
} from './types';
import { CodePanel, COLORS, Field, healthColor, StatusChip, syncColor } from './ui';

const PATCH_TYPES = [
  'application/merge-patch+json',
  'application/strategic-merge-patch+json',
  'application/json-patch+json',
];

function LogsTab({
  api,
  application,
  resource,
  containers,
}: {
  api: ArgoCdApi;
  application: ArgoApplication;
  resource: ArgoResource;
  containers: string[];
}) {
  const [container, setContainer] = useState(containers[0] ?? '');
  const [tail, setTail] = useState(500);
  const [since, setSince] = useState(0);
  const [previous, setPrevious] = useState(false);
  const [lines, setLines] = useState<ArgoLogLine[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!resource.name || !resource.namespace) return;
    setLoading(true);
    setError(null);
    try {
      setLines(
        await api.logs(application, {
          podName: resource.name,
          namespace: resource.namespace,
          container: container || undefined,
          tailLines: tail,
          sinceSeconds: since || undefined,
          previous,
        }),
      );
    } catch (logsError) {
      setError(
        logsError instanceof Error ? logsError.message : String(logsError),
      );
    } finally {
      setLoading(false);
    }
  }, [api, application, resource, container, tail, since, previous]);

  useEffect(() => {
    load();
  }, [load]);

  const download = () => {
    const blob = new Blob(
      [(lines ?? []).map(line => line.content).join('\n')],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${resource.name}-${container || 'logs'}.log`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 1.5, flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
      >
        <TextField
          select
          size="small"
          label="Container"
          value={container}
          onChange={event => setContainer(event.target.value)}
          sx={{ minWidth: 160 }}
        >
          {containers.map(name => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Tail"
          value={tail}
          onChange={event => setTail(Number(event.target.value))}
          sx={{ width: 110 }}
        >
          {[100, 500, 1000, 5000].map(value => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Since"
          value={since}
          onChange={event => setSince(Number(event.target.value))}
          sx={{ width: 130 }}
        >
          <MenuItem value={0}>All time</MenuItem>
          <MenuItem value={300}>5 minutes</MenuItem>
          <MenuItem value={3600}>1 hour</MenuItem>
          <MenuItem value={14400}>4 hours</MenuItem>
        </TextField>
        <FormControlLabel
          sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
          control={
            <Checkbox
              size="small"
              checked={previous}
              onChange={event => setPrevious(event.target.checked)}
            />
          }
          label="Previous"
        />
        <IconButton size="small" onClick={load} disabled={loading}>
          <RefreshIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton size="small" onClick={download} disabled={!lines?.length}>
          <DownloadIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            maxHeight: 480,
            overflow: 'auto',
            border: `1px solid ${COLORS.border}`,
            borderRadius: 1,
            bgcolor: '#0b1020',
            color: COLORS.text,
            fontFamily: 'monospace',
            fontSize: 11,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {lines?.length
            ? lines.map(line => line.content).join('\n')
            : 'No log output'}
        </Box>
      )}
    </Box>
  );
}

export function ResourceDialog({
  api,
  application,
  resource,
  details,
  loading,
  error,
  onClose,
  onNotify,
  onChanged,
  tab,
  onTabChange,
}: {
  api: ArgoCdApi;
  application: ArgoApplication | null;
  resource: ArgoResource | null;
  details: ArgoResourceDetails | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onNotify: (severity: 'success' | 'error', message: string) => void;
  onChanged: () => void;
  /** Owned by the page so the open tab is part of the URL. */
  tab: ArgoResourceTab;
  onTabChange: (tab: ArgoResourceTab) => void;
}) {
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [actions, setActions] = useState<ArgoResourceAction[] | null>(null);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteForce, setDeleteForce] = useState(false);
  const [deleteOrphan, setDeleteOrphan] = useState(false);
  const [patchOpen, setPatchOpen] = useState(false);
  const [patchType, setPatchType] = useState(PATCH_TYPES[0]);
  const [patchBody, setPatchBody] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setActions(null);
    setDeleteOpen(false);
    setPatchOpen(false);
    setConfirmAction(null);
  }, [resource]);

  const containers = useMemo(() => {
    const spec = (details?.liveState as any)?.spec;
    return [
      ...(spec?.initContainers ?? []),
      ...(spec?.containers ?? []),
    ]
      .map((entry: any) => entry?.name)
      .filter(Boolean);
  }, [details]);

  const isPod = resource?.kind === 'Pod' && !resource.group;

  // A link can name the Logs tab on something that is not a pod; fall back
  // rather than render an empty panel.
  useEffect(() => {
    if (tab === 'logs' && !isPod) onTabChange('summary');
  }, [tab, isPod, onTabChange]);

  const openActions = async (anchor: HTMLElement) => {
    if (!application || !resource) return;
    setActionsAnchor(anchor);
    if (actions) return;
    setActionsLoading(true);
    try {
      setActions(await api.resourceActions(application, resource));
    } catch (actionsError) {
      onNotify(
        'error',
        actionsError instanceof Error
          ? actionsError.message
          : String(actionsError),
      );
      setActionsAnchor(null);
    } finally {
      setActionsLoading(false);
    }
  };

  const runAction = async (action: string) => {
    if (!application || !resource) return;
    setBusy(true);
    setConfirmAction(null);
    try {
      await api.runResourceAction(application, resource, action);
      onNotify('success', `Action '${action}' executed on ${resource.name}`);
      onChanged();
    } catch (actionError) {
      onNotify(
        'error',
        actionError instanceof Error ? actionError.message : String(actionError),
      );
    } finally {
      setBusy(false);
    }
  };

  const deleteResource = async () => {
    if (!application || !resource) return;
    setBusy(true);
    try {
      await api.deleteResource(application, resource, {
        force: deleteForce,
        orphan: deleteOrphan,
      });
      onNotify('success', `Delete requested for ${resource.name}`);
      setDeleteOpen(false);
      onChanged();
      onClose();
    } catch (deleteError) {
      onNotify(
        'error',
        deleteError instanceof Error ? deleteError.message : String(deleteError),
      );
    } finally {
      setBusy(false);
    }
  };

  const patchResource = async () => {
    if (!application || !resource || !patchBody.trim()) return;
    setBusy(true);
    try {
      await api.patchResource(application, resource, patchBody, patchType);
      onNotify('success', `Patched ${resource.name}`);
      setPatchOpen(false);
      setPatchBody('');
      onChanged();
    } catch (patchError) {
      onNotify(
        'error',
        patchError instanceof Error ? patchError.message : String(patchError),
      );
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: ArgoResourceTab; label: string }[] = [
    { id: 'summary', label: 'Summary' },
    { id: 'diff', label: 'Diff' },
    { id: 'live', label: 'Live manifest' },
    { id: 'desired', label: 'Desired manifest' },
    { id: 'events', label: `Events (${details?.events.length ?? 0})` },
    ...(isPod
      ? [{ id: 'logs' as ArgoResourceTab, label: 'Logs' }]
      : []),
  ];

  return (
    <Dialog open={Boolean(resource)} onClose={onClose} fullWidth maxWidth="lg">
      {resource && (
        <>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{ color: COLORS.text, fontSize: 18, fontWeight: 700 }}
                >
                  {resource.name}
                </Typography>
                <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                  {resource.group ? `${resource.group}/` : ''}
                  {resource.version} · {resource.kind} ·{' '}
                  {resource.namespace ?? 'cluster-scoped'}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<BoltIcon sx={{ fontSize: 16 }} />}
                disabled={busy}
                onClick={event => openActions(event.currentTarget)}
                sx={{ textTransform: 'none' }}
              >
                Actions
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                disabled={busy}
                onClick={() => setPatchOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Patch
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                disabled={busy}
                onClick={() => setDeleteOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Delete
              </Button>
              <IconButton onClick={onClose} aria-label="Close resource details">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}
            >
              {resource.status && (
                <StatusChip
                  label={resource.status}
                  color={syncColor(resource.status)}
                />
              )}
              {resource.healthStatus && (
                <StatusChip
                  label={resource.healthStatus}
                  color={healthColor(resource.healthStatus)}
                />
              )}
              {resource.requiresPruning && (
                <StatusChip label="Requires pruning" color={COLORS.amber} />
              )}
              {resource.orphaned && (
                <StatusChip label="Orphaned" color={COLORS.red} />
              )}
            </Stack>

            {resource.healthMessage && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                {resource.healthMessage}
              </Alert>
            )}

            <Tabs
              value={tab}
              onChange={(_event, value) =>
                onTabChange(value as ArgoResourceTab)
              }
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2, borderBottom: `1px solid ${COLORS.border}` }}
            >
              {tabs.map(entry => (
                <Tab key={entry.id} label={entry.label} value={entry.id} />
              ))}
            </Tabs>

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
              </Box>
            )}
            {error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && tab === 'summary' && (
              <Box>
                <Field label="Kind" value={resource.kind} />
                <Field label="API group" value={resource.group || 'core'} />
                <Field label="Version" value={resource.version} />
                <Field label="Namespace" value={resource.namespace} />
                <Field label="Name" value={resource.name} />
                <Field label="UID" value={resource.uid} />
                <Field label="Sync status" value={resource.status} />
                <Field label="Health" value={resource.healthStatus} />
                <Field label="Health message" value={resource.healthMessage} />
                <Field
                  label="Images"
                  value={
                    resource.images.length
                      ? resource.images.join(', ')
                      : undefined
                  }
                />
                {resource.info.map(item => (
                  <Field key={item.name} label={item.name} value={item.value} />
                ))}
              </Box>
            )}
            {!loading && !error && tab === 'diff' && (
              <DiffView
                before={details?.normalizedLiveState}
                after={details?.predictedLiveState}
              />
            )}
            {!loading && !error && tab === 'live' && (
              <CodePanel value={details?.liveState} />
            )}
            {!loading && !error && tab === 'desired' && (
              <CodePanel value={details?.targetState} />
            )}
            {!loading && !error && tab === 'events' && (
              <TableContainer
                sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 1 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Last seen</TableCell>
                      <TableCell>Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(details?.events ?? []).map((event, index) => (
                      <TableRow
                        key={`${event.reason}-${event.lastTimestamp}-${index}`}
                      >
                        <TableCell>
                          <StatusChip
                            label={event.type ?? 'Unknown'}
                            color={
                              event.type === 'Warning'
                                ? COLORS.red
                                : COLORS.green
                            }
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {event.reason ?? '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {event.message ?? '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {event.source ?? '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {event.lastTimestamp
                            ? new Date(event.lastTimestamp).toLocaleString()
                            : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {event.count ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {details?.events.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No Kubernetes events
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {!loading && !error && isPod && tab === 'logs' && application && (
              <LogsTab
                api={api}
                application={application}
                resource={resource}
                containers={containers}
              />
            )}
          </DialogContent>

          <Menu
            anchorEl={actionsAnchor}
            open={Boolean(actionsAnchor)}
            onClose={() => setActionsAnchor(null)}
          >
            {actionsLoading && (
              <MenuItem disabled>
                <CircularProgress size={14} sx={{ mr: 1 }} /> Loading…
              </MenuItem>
            )}
            {actions?.length === 0 && (
              <MenuItem disabled>No actions available</MenuItem>
            )}
            {(actions ?? []).map(action => (
              <MenuItem
                key={action.name}
                disabled={action.disabled}
                onClick={() => {
                  setActionsAnchor(null);
                  setConfirmAction(action.name);
                }}
              >
                {action.displayName ?? action.name}
              </MenuItem>
            ))}
          </Menu>

          <Dialog
            open={Boolean(confirmAction)}
            onClose={() => setConfirmAction(null)}
          >
            <DialogTitle>Run '{confirmAction}'?</DialogTitle>
            <DialogContent>
              <Typography sx={{ fontSize: 13 }}>
                Run action <strong>{confirmAction}</strong> on{' '}
                <strong>
                  {resource.kind}/{resource.name}
                </strong>
                ?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={() => confirmAction && runAction(confirmAction)}
              >
                Run
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
            <DialogTitle>
              Delete {resource.kind}/{resource.name}?
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ mb: 1, fontSize: 13 }}>
                The resource is deleted from the cluster. If it is still in
                Git, Argo CD recreates it on the next sync.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={deleteForce}
                    onChange={event => setDeleteForce(event.target.checked)}
                  />
                }
                label="Force delete (skip finalizers)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={deleteOrphan}
                    onChange={event => setDeleteOrphan(event.target.checked)}
                  />
                }
                label="Orphan dependents"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="error"
                disabled={busy}
                onClick={deleteResource}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={patchOpen}
            onClose={() => setPatchOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              Patch {resource.kind}/{resource.name}
            </DialogTitle>
            <DialogContent>
              <TextField
                select
                fullWidth
                size="small"
                label="Patch type"
                value={patchType}
                onChange={event => setPatchType(event.target.value)}
                sx={{ mt: 0.5, mb: 1.5 }}
              >
                {PATCH_TYPES.map(type => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                multiline
                minRows={8}
                value={patchBody}
                onChange={event => setPatchBody(event.target.value)}
                placeholder='{"spec": {"replicas": 3}}'
                InputProps={{
                  sx: { fontFamily: 'monospace', fontSize: 12 },
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPatchOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                disabled={busy || !patchBody.trim()}
                onClick={patchResource}
              >
                Patch
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Dialog>
  );
}
